import React from 'react';
import { Box, Typography, TextField, FormControl, Select, MenuItem, CircularProgress, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { fieldSx, labelSx, multilineFieldSx, menuProps } from '../../styles/formStyles';

const ESTADOS = [
  { value: 'pendiente',    label: 'Pendiente',    dot: '#90a4ae' },
  { value: 'confirmado',   label: 'Confirmado',   dot: '#42a5f5' },
  { value: 'en_recepcion', label: 'En recepción', dot: '#66bb6a' },
  { value: 'demorado',     label: 'Demorado',     dot: '#ffa726' },
  { value: 'atendido',     label: 'Atendido',     dot: '#26a69a' },
  { value: 'cancelado',    label: 'Cancelado',    dot: '#ef5350' },
];

function LF({ label, children }) {
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function TabDatos({ form, setForm, medicos = [], obrasSociales = [], buscandoDni, estadoDni, setEstadoDni, buscarPorDni, readOnly = false, rol = '', onCrearPaciente }) {
  const f = (field) => e => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const estadosDisponibles = rol === 'recepcionista'
    ? ESTADOS.filter(e => e.value !== 'atendido')
    : ESTADOS;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* Médico */}
      <LF label="Médico">
        <FormControl fullWidth sx={fieldSx} disabled={readOnly || rol === 'medico'}>
          <Select displayEmpty value={form.medico_id} onChange={f('medico_id')}
            sx={{ color: '#e0e0e0' }} MenuProps={menuProps}>
            <MenuItem value=""><em style={{ color: '#888' }}>Seleccioná un médico</em></MenuItem>
            {medicos.map(m => (
              <MenuItem key={m.id} value={m.id}>
                {m.nombre} {m.apellido}{m.especialidad ? ` — ${m.especialidad}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </LF>

      {/* Grilla Paciente | Turno */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

        {/* Columna Paciente */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#607d8b', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Paciente
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {/* DNI con auto-lookup */}
            <Box>
              <Typography sx={labelSx}>DNI</Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField fullWidth value={form.paciente_dni} disabled={readOnly}
                  onChange={e => { setForm(prev => ({ ...prev, paciente_dni: e.target.value })); setEstadoDni(null); }}
                  onBlur={() => buscarPorDni(form.paciente_dni)}
                  inputProps={{ style: { paddingRight: 32 } }}
                  sx={fieldSx} />
                {buscandoDni && (
                  <CircularProgress size={16} sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#90a4ae' }} />
                )}
                {!buscandoDni && estadoDni === 'encontrado' && (
                  <CheckCircleIcon sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#66bb6a' }} />
                )}
                {!buscandoDni && estadoDni === 'no_encontrado' && (
                  <ErrorIcon sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#ef5350' }} />
                )}
              </Box>
              {!buscandoDni && estadoDni === 'no_encontrado' && onCrearPaciente && (
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 11, color: '#ef9a9a' }}>Paciente no encontrado.</Typography>
                  <Button size="small" startIcon={<PersonAddIcon sx={{ fontSize: 13 }} />}
                    onClick={onCrearPaciente}
                    sx={{ fontSize: 11, color: '#81c784', p: 0, minWidth: 0, textTransform: 'none',
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                    Crear paciente
                  </Button>
                </Box>
              )}
            </Box>
            <LF label="Teléfono">
              <TextField fullWidth value={form.paciente_telefono} onChange={f('paciente_telefono')} disabled={readOnly} sx={fieldSx} />
            </LF>
          </Box>

          <LF label="Nombre completo">
            <TextField fullWidth value={form.paciente_nombre} onChange={f('paciente_nombre')} disabled={readOnly} sx={fieldSx} />
          </LF>
          <LF label="Email">
            <TextField fullWidth value={form.paciente_email} onChange={f('paciente_email')} disabled={readOnly} sx={fieldSx} />
          </LF>
          {!form.paciente_email?.trim() && !readOnly && (
            <Typography sx={{ fontSize: 11, color: '#ffb74d' }}>
              ⚠️ Este paciente no tiene correo electrónico registrado.
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <LF label="Obra social">
              <FormControl fullWidth sx={fieldSx} disabled={readOnly}>
                <Select displayEmpty value={form.paciente_obra_social} onChange={f('paciente_obra_social')}
                  sx={{ color: '#e0e0e0' }} MenuProps={menuProps}>
                  <MenuItem value=""><em style={{ color: '#888' }}>Sin obra social</em></MenuItem>
                  {obrasSociales.map(os => (
                    <MenuItem key={os.id ?? os} value={os.nombre ?? os}>{os.nombre ?? os}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LF>
            <LF label="Número de afiliado">
              <TextField fullWidth value={form.paciente_numero_afiliado} onChange={f('paciente_numero_afiliado')} disabled={readOnly} sx={fieldSx} />
            </LF>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <LF label="Fecha de nacimiento">
              <TextField type="date" fullWidth value={form.paciente_fecha_nacimiento} onChange={f('paciente_fecha_nacimiento')} disabled={readOnly}
                inputProps={{ style: { colorScheme: 'dark' } }} sx={fieldSx} />
            </LF>
            <LF label="Dirección">
              <TextField fullWidth value={form.paciente_direccion} onChange={f('paciente_direccion')} disabled={readOnly} sx={fieldSx} />
            </LF>
          </Box>
        </Box>

        {/* Columna Turno */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#607d8b', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Turno
          </Typography>

          <LF label="Fecha">
            <TextField type="date" fullWidth value={form.fecha} onChange={f('fecha')} disabled={readOnly}
              inputProps={{ style: { colorScheme: 'dark' } }} sx={fieldSx} />
          </LF>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <LF label="Hora">
              <TextField type="time" fullWidth value={form.hora_inicio} onChange={f('hora_inicio')} disabled={readOnly}
                inputProps={{ style: { colorScheme: 'dark' } }} sx={fieldSx} />
            </LF>
            <LF label="Duración (min)">
              <TextField type="number" fullWidth value={form.duracion} onChange={f('duracion')} disabled={readOnly} sx={fieldSx} />
            </LF>
          </Box>

          <LF label="Estado">
            <FormControl fullWidth sx={fieldSx} disabled={readOnly}>
              <Select displayEmpty value={form.estado} onChange={f('estado')}
                sx={{ color: '#e0e0e0' }} MenuProps={menuProps}>
                {estadosDisponibles.map(e => (
                  <MenuItem key={e.value} value={e.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: e.dot, flexShrink: 0 }} />
                      {e.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </LF>

          <LF label="Motivo">
            <TextField fullWidth value={form.motivo} onChange={f('motivo')} disabled={readOnly} sx={fieldSx} />
          </LF>

          <LF label="Notas internas">
            <TextField multiline rows={3} fullWidth value={form.notas} onChange={f('notas')} disabled={readOnly} sx={multilineFieldSx} />
          </LF>
        </Box>
      </Box>

      {/* Mensaje del paciente + nota para el médico */}
      <Box sx={{ mt: 1 }}>
        {form.mensaje_paciente && (
          <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: '#0d1b2a', border: '1px solid #1e3a5f' }}>
            <Typography sx={{ fontSize: 10, color: '#546e7a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
              Mensaje del paciente
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#90a4ae', fontStyle: 'italic' }}>
              "{form.mensaje_paciente}"
            </Typography>
          </Box>
        )}
        <LF label="Nota para el médico">
          <TextField multiline rows={2} fullWidth value={form.nota_medico} onChange={f('nota_medico')} disabled={readOnly}
            sx={{ ...multilineFieldSx, '& .MuiOutlinedInput-root': { ...multilineFieldSx['& .MuiOutlinedInput-root'], '& fieldset': { borderColor: '#1b5e20' }, '&:hover fieldset': { borderColor: '#2e7d32' }, '&.Mui-focused fieldset': { borderColor: '#43a047' } } }} />
        </LF>
      </Box>
    </Box>
  );
}
